import { useState } from "react";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    q: "How does the initial consultation work? Is it in person or virtual?",
    a: "Your initial consultation is conducted conveniently over a Zoom web conference call with our clinical care team. As your steps evolve, you will also have the option to meet with our psychiatrist either by Zoom web conference or in person at our clinic. The only mandatory in-person steps are the resting-state fMRI brain mapping and the five-day treatment series itself.",
  },
  {
    q: "Which steps of SAINT treatment must be completed in person?",
    a: "The two mandatory in-person steps are: (1) the resting-state fMRI brain scan (about 45 minutes at an imaging partner facility), and (2) the five-day treatment week at our Coral Springs clinic (ten 10-minute sessions per day with 50-minute breaks in our private lounge suites). Initial consultations and ongoing psychiatric evaluations can be done via Zoom web conference or in person.",
  },
  {
    q: "Do I need my own fMRI first?",
    a: "No. We order and coordinate the resting-state fMRI scan for you. You do not need to arrange or bring your own scan.",
  },
  {
    q: "Do I have to stop my medications?",
    a: "Usually no. Do not change or stop any medication without first discussing it with your prescribing doctor.",
  },
  {
    q: "What does SAINT TMS feel like?",
    a: "Most people feel a tapping sensation and hear a clicking sound from the magnetic coil. There is no sedation and you remain awake and alert throughout.",
  },
  {
    q: "Can I drive myself to and from treatment?",
    a: "Yes. SAINT is non-invasive and does not require sedation, so you can drive yourself to and from each treatment day.",
  },
  {
    q: "What happens if I don't reach remission by day five?",
    a: "Your care team and psychiatrist will discuss follow-up options and any appropriate next steps based on your individual response to treatment, available either in person or via Zoom.",
  },
  {
    q: "How do I access SAINT® — insurance or self-pay?",
    a: "There are two pathways. We accept Blue Cross Blue Shield in select states and the Blue Cross Blue Shield Federal Employee Program in select states; because insurance requires a review and approval process, this pathway generally takes longer from consultation to treatment. Our most common option is self-pay, which typically ranges from $25,000 to $28,000 and offers the greatest speed and flexibility — most patients move from consultation to treatment within a few days, with appointments available seven days a week, extended hours, and private treatment suites. Coverage varies by state and plan and is not guaranteed. Contact us to determine eligibility and the fastest path to care for your situation.",
  },
  {
    q: "Is SAINT the same as accelerated TMS at other clinics?",
    a: "Only Verified SAINT Providers use the FDA-cleared SAINT Neuromodulation System with fMRI-guided targeting. You can view our official certification and verified provider details at /find-a-verified-saint-provider.",
  },
  {
    q: "What is the FDA status of SAINT®?",
    a: "SAINT® received FDA Breakthrough Device Designation and was cleared under 510(k) (K220177) in 2022 for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication in the current episode. It is the only FDA-cleared depression treatment that uses your own fMRI brain imaging to personalize the stimulation target.",
  },
  {
    q: "Do the results last beyond the treatment week?",
    a: "Published research includes a study demonstrating sustained remission at 12 months when SAINT® is paired with a personalized continuation and maintenance therapy plan. Your care team builds an individualized follow-up strategy to help protect the progress you make during your five-day treatment week.",
  },
];

export const SaintFaq = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <section className="py-16 sm:py-20 bg-muted/30" id="faq">
      <div className="container-px mx-auto max-w-3xl px-4">
        <div className="text-center mb-10">
          <h2 className="font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
            Frequently asked questions
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((item, idx) => (
            <div
              key={idx}
              className="rounded-xl border border-border bg-card shadow-soft overflow-hidden"
            >
              <button
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openFaq === idx}
                aria-controls={`faq-panel-${idx}`}
                id={`faq-trigger-${idx}`}
              >
                <span className="font-headings text-sm font-bold text-primary sm:text-base">
                  {item.q}
                </span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-accent transition-transform ${
                    openFaq === idx ? "rotate-180" : ""
                  }`}
                />
              </button>
              <div
                id={`faq-panel-${idx}`}
                role="region"
                aria-labelledby={`faq-trigger-${idx}`}
                className={`px-5 text-sm text-foreground/80 leading-relaxed transition-all ${
                  openFaq === idx ? "block pb-4" : "hidden"
                }`}
              >
                {item.a}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
